import {DataGridPro} from "@mui/x-data-grid-pro";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Collapse,
  Divider,
  FormControlLabel,
  Menu,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {useTheme} from "@mui/material/styles";
import {useCallback, useEffect, useRef, useState} from "react";
import {teamStrengthColumns} from "../parts/teamStrengthColumns";
import {computeTeamStrengthMutation} from "../helpers/computeTeamStrengthMutation";
import {computeInterdependentValues} from "../helpers/computeInterdependentValues";
import {
  adjustDrawFactor,
  adjustGoalAverage,
  adjustHomeAdvantage,
  average,
  calculateRecommendation,
  normalizeGoalTotals,
  normalizeHomeAdvantage,
  setDrawFactor,
  setGoalAverage,
  setHomeAdvantage,
} from "../helpers/adjustTeamStrengths";
import {sortAndRankTeams} from "../helpers/sortAndRankTeams";
import {loadPreviousLeagueSeason} from "../helpers/previousLeagueSeason";
import {fetchViaPost} from "../helpers/fetchViaPost";
import {fetchFromApi} from "../helpers/fetchFromApi";
import DeleteIcon from '@mui/icons-material/Delete';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';

export function TeamStrengthEditor({
                                     user_id,
                                     season_id,
                                     teams,
                                     rows,
                                     setRows,
                                     apiRef,
                                     setHasChanges,
                                     setCloseParent,
                                     seasonParamsReloadKey,
                                     mode = "full",
                                     onRowsChange = null,
                                   }) {
  useEffect(() => {
    setRows(sortAndRankTeams(teams));
  }, [teams, setRows]);

  /*
   * ==========================================================
   * EDITOR MODE
   * ==========================================================
   *
   * full
   *   Vollständige Team-Settings-Schaltzentrale.
   *
   * simulation
   *   Vereinfachte Ansicht für einen Simulationslauf.
   *   Änderungen bleiben zunächst ausschließlich lokal.
   *
   * Wichtig:
   * Der TeamStrengthEditor speichert selbst keine Team Settings.
   * Dauerhaftes Speichern bleibt Aufgabe des übergeordneten
   * Dialogs und muss ausdrücklich ausgelöst werden.
   */
  const isSimulationMode =
    mode === "simulation";

  /*
   * Der Simulationsdialog benötigt exakt die Werte,
   * die gerade im Editor sichtbar sind – auch wenn sie
   * noch nicht dauerhaft gespeichert wurden.
   *
   * Dadurch können wir später:
   *
   * - mit gespeicherten Team Settings simulieren,
   * - Werte spielerisch verändern,
   * - mit den temporären Werten simulieren,
   * - und nur auf ausdrücklichen Wunsch speichern.
   */
  useEffect(() => {
    if (
      typeof onRowsChange === "function"
    ) {
      onRowsChange(rows);
    }
  }, [rows, onRowsChange]);

  const noButtonRef = useRef(null);
  const [panelHeaderValue, setPanelHeaderValue] = useState(null);
  const [promiseArguments, setPromiseArguments] = useState(null);

  const [seasonParams, setSeasonParams] = useState([]);

  const [
    previousSeason,
    setPreviousSeason,
  ] = useState(null);

  const [
    previousSeasonReference,
    setPreviousSeasonReference,
  ] = useState(null);

  const [
    loadingPreviousSeason,
    setLoadingPreviousSeason,
  ] = useState(false);

  const [
    calculatingPreviousSeason,
    setCalculatingPreviousSeason,
  ] = useState(false);

  const [open, setOpen] = useState(false);

  const [
    adjustmentMenuAnchor,
    setAdjustmentMenuAnchor,
  ] = useState(null);

  const [
    adjustmentMenuType,
    setAdjustmentMenuType,
  ] = useState(null);

  const [
    adjustmentDialog,
    setAdjustmentDialog,
  ] = useState(null);

  const [
    normalizationMenuAnchor,
    setNormalizationMenuAnchor,
  ] = useState(null);

  const [
    normalizationDialog,
    setNormalizationDialog,
  ] = useState(null);

  const [targetValue, setTargetValue] =
    useState("");

  const [
    consistencyOpen,
    setConsistencyOpen,
  ] = useState(false);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClickDelete = async () => {
    const request = await fetchViaPost("delete-league", season_id);
    setOpen(false);
    setCloseParent(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    async function fetchData() {
      try {
        let response = await fetchViaPost(
          "get-season-parameters",
          JSON.stringify({user_id, season_id})
        );
        setSeasonParams(JSON.parse(await response.json()));
      } catch (err) {
        //console.log(err);
      }
    }

    fetchData();
  }, [user_id, season_id, seasonParamsReloadKey]);

  async function loadPreviousSeasonReference() {
    if (!season_id) {
      return;
    }

    setLoadingPreviousSeason(true);

    try {
      /*
       * Aktuelle Saison laden, damit wir ihre
       * Liga und ihr Startdatum kennen.
       */
      const {currentSeason, previousSeason: previous} =
        await loadPreviousLeagueSeason(season_id);

      if (
        !currentSeason?.league_id ||
        !currentSeason?.starting_at
      ) {
        setPreviousSeason(null);
        setPreviousSeasonReference(null);
        return;
      }

      setPreviousSeason(previous);

      if (!previous) {
        setPreviousSeasonReference(null);
        return;
      }

      /*
       * Genau den League-Reference-Record
       * dieser Vorsaison lesen.
       */
      const response =
        await fetchViaPost(
          "get-league-reference-stats",
          JSON.stringify({
            league_id:
              Number(
                currentSeason.league_id
              ),

            season_id:
              Number(previous.id),
          })
        );

      const result =
        await response.json();

      if (
        result?.success &&
        result?.found &&
        result?.aggregate
      ) {
        setPreviousSeasonReference(
          result
        );
      } else {
        setPreviousSeasonReference(null);
      }

    } catch (error) {
      console.error(
        "[TeamStrengthEditor] previous season",
        error
      );

      setPreviousSeason(null);
      setPreviousSeasonReference(null);
    } finally {
      setLoadingPreviousSeason(false);
    }
  }

  useEffect(() => {
    loadPreviousSeasonReference();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [season_id, user_id]);


  async function calculatePreviousSeasonReference() {
    if (!previousSeason) {
      return;
    }

    setCalculatingPreviousSeason(true);

    try {
      const currentResponse = await (
        await fetchFromApi(
          `fetch-data-v3/seasons/${season_id}?include=league`
        )
      ).json();

      const leagueId =
        Number(
          currentResponse?.data?.league_id
        );

      if (!Number.isFinite(leagueId)) {
        throw new Error(
          "League-ID der aktuellen Saison konnte nicht bestimmt werden."
        );
      }

      const response =
        await fetchViaPost(
          "recalculate-league-reference-stats",
          JSON.stringify({
            league_id: leagueId,
            season_id:
              Number(previousSeason.id),
          })
        );

      const result =
        await response.json();

      if (!result?.success) {
        throw new Error(
          result?.message ||
          "Vorsaison konnte nicht berechnet werden."
        );
      }

      /*
       * Danach den tatsächlich gespeicherten
       * Record neu lesen.
       */
      await loadPreviousSeasonReference();

      setSnackbar({
        severity: "success",
        children:
          "League Reference Stats der Vorsaison wurden berechnet.",
      });

    } catch (error) {
      setSnackbar({
        severity: "error",
        children:
          error?.message ||
          String(error),
      });
    } finally {
      setCalculatingPreviousSeason(false);
    }
  }


  function getPreviousSeasonDrawRecommendation() {
    const model =
      previousSeasonReference
        ?.aggregate
        ?.model_comparison;

    const expectedDrawRate =
      Number(
        model?.expected_draw_rate
      );

    const actualDrawRate =
      Number(
        model?.actual?.draw_rate
      );

    const storedDrawFactor =
      Number(
        seasonParams?.drawfactor
      );

    if (
      !Number.isFinite(
        expectedDrawRate
      ) ||
      !Number.isFinite(
        actualDrawRate
      ) ||
      !Number.isFinite(
        storedDrawFactor
      )
    ) {
      return null;
    }

    /*
     * Wichtig:
     *
     * Mehr Remis als erwartet
     * -> Draw-Factor SENKEN.
     *
     * Weniger Remis als erwartet
     * -> Draw-Factor ERHÖHEN.
     *
     * Erste Arbeitsnäherung:
     * 1 Prozentpunkt Abweichung
     * entspricht 0,015 Draw-Factor.
     */
    const difference =
      actualDrawRate -
      expectedDrawRate;

    const adjustment =
      difference * 1.5;

    const recommendation =
      storedDrawFactor -
      adjustment;

    /*
     * Draw-Factor bewusst in einem
     * vernünftigen Arbeitsbereich halten.
     */
    return Math.min(
      0.90,
      Math.max(
        0.83,
        recommendation
      )
    );
  }


  function getPreviousSeasonValue(type) {
    const actual =
      previousSeasonReference
        ?.aggregate
        ?.actual;

    if (!actual) {
      return null;
    }

    if (type === "goal_average") {
      const value =
        Number(actual.goal_average);

      return Number.isFinite(value)
        ? value
        : null;
    }

    if (type === "home_advantage") {
      const value =
        Number(actual.home_advantage);

      return Number.isFinite(value)
        ? value
        : null;
    }

    /*
     * draw_rate ist NICHT unser draw_factor.
     * Deshalb wird für diesen Utility-Typ
     * bewusst kein Vorsaisonwert verwendet.
     */
    return null;
  }


  const [snackbar, setSnackbar] = useState(null);

  const handleCloseSnackbar = () => setSnackbar(null);
  const [checked, setChecked] = useState(false);

  const handleChange = (event) => {
    setChecked(event.target.checked);
  };
  const processRowUpdate = useCallback(
    (newRow, oldRow) => {
      return new Promise((resolve, reject) => {
        const mutation = computeTeamStrengthMutation(newRow, oldRow);
        if (mutation) {
          setHasChanges(true);
          setPromiseArguments({ resolve, reject, newRow, oldRow });
        } else {
          resolve(oldRow);
        }
      });
    },
    [setHasChanges]
  );

  const formatSettingValue = (
    value,
    digits = 3
  ) => {
    const number =
      Number(value);

    return Number.isFinite(number)
      ? number.toFixed(digits)
      : "–";
  };


  const panelHeader = () => {
    const goalAverage =
      Number(
        seasonParams
          ?.stored_goal_average
      );

    const homeAdvantage =
      Number(
        seasonParams
          ?.exp_home_factor
      );

    const drawFactor =
      Number(
        seasonParams
          ?.drawfactor
      );

    return (
      <Box
        sx={{
          mt: 2,
          mb: 2.5,
          p: 2.5,
          border: 1,
          borderColor: "divider",
          borderRadius: 2,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            mb: 0.5,
            fontWeight: 700,
          }}
        >
          Season Parameters
        </Typography>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 2 }}
        >
          Diese ligaweiten Werte werden aus
          den Team Settings abgeleitet und
          bilden gemeinsam mit ihnen die
          Grundlage der Berechnungen.
        </Typography>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(3, minmax(0, 1fr))",
            },
            gap: 1.5,
          }}
        >
          <Tooltip
            arrow
            title="Erwarteter durchschnittlicher Torschnitt der Saison."
          >
            <Box
              sx={{
                p: 1.75,
                border: 1,
                borderColor: "divider",
                borderRadius: 1.5,
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
              >
                Torschnitt
              </Typography>

              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  fontVariantNumeric:
                    "tabular-nums",
                }}
              >
                {Number.isFinite(
                  goalAverage
                )
                  ? goalAverage.toFixed(3)
                  : "–"}
              </Typography>
            </Box>
          </Tooltip>

          <Tooltip
            arrow
            title="Ligaweiter Heimvorteil. Der Wert ist eine zentrale Season-Parameter-Kennzahl."
          >
            <Box
              sx={{
                p: 1.75,
                border: 1,
                borderColor: "divider",
                borderRadius: 1.5,
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
              >
                Heimvorteil
              </Typography>

              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  fontVariantNumeric:
                    "tabular-nums",
                }}
              >
                {Number.isFinite(
                  homeAdvantage
                )
                  ? homeAdvantage.toFixed(3)
                  : "–"}
              </Typography>
            </Box>
          </Tooltip>

          <Tooltip
            arrow
            title="Ligaweiter Unentschieden-Faktor. Je kleiner der Wert, desto stärker ist die Remis-Neigung."
          >
            <Box
              sx={{
                p: 1.75,
                border: 1,
                borderColor: "divider",
                borderRadius: 1.5,
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
              >
                Unentschieden-Faktor
              </Typography>

              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  fontVariantNumeric:
                    "tabular-nums",
                }}
              >
                {Number.isFinite(
                  drawFactor
                )
                  ? drawFactor.toFixed(3)
                  : "–"}
              </Typography>
            </Box>
          </Tooltip>
        </Box>
      </Box>
    );
  };


  const getCombinedHomeAdvantage = (row) => {
    const forHome =
      Number(
        row.for_homefactor
      );

    const againstHome =
      Number(
        row.against_homefactor
      );

    if (
      !Number.isFinite(
        forHome
      ) ||
      !Number.isFinite(
        againstHome
      )
    ) {
      return null;
    }

    /*
     * Gemeinsame Team-Kennzahl.
     *
     * Beispiel Bayern:
     * For Homefactor     1.084
     * Against Homefactor 1.006
     *
     * Differenz:
     * 0.078 / 2 = 0.039
     *
     * -> Heimvorteil 1.039
     */
    return (
      1 +
      (
        forHome -
        againstHome
      ) /
      2
    );
  };


  const updateCompactSetting = (
    row,
    field,
    rawValue
  ) => {
    const value =
      Number(
        rawValue
      );

    if (
      !Number.isFinite(
        value
      )
    ) {
      return;
    }

    let updatedRow = {
      ...row,
    };


    if (
      field ===
      "total_strength"
    ) {
      /*
       * Spielstärke / Torquotient.
       *
       * Die bestehende detaillierte Mutation
       * bleibt für die Full-Ansicht erhalten.
       *
       * In der kompakten Ansicht wird zunächst
       * nur der sichtbare Wert gesetzt.
       */
      updatedRow.total_strength =
        value;
    }


    if (
      field ===
      "total_goals"
    ) {
      updatedRow.total_goals =
        value;
    }


    if (
      field ===
      "draw_factor"
    ) {
      updatedRow.draw_factor =
        value;
    }


    if (
      field ===
      "home_advantage"
    ) {
      const delta =
        value -
        1;

      updatedRow.for_homefactor =
        1 +
        delta;

      updatedRow.against_homefactor =
        1 -
        delta;
    }


    setRows(
      sortAndRankTeams(
        rows.map(
          (item) =>
            item.id ===
            row.id
              ? updatedRow
              : item
        )
      )
    );

    setHasChanges(
      true
    );
  };


  const compactNumberField = (
    row,
    field,
    value
  ) => (
    <TextField
      size="small"
      type="number"
      defaultValue={
        Number.isFinite(
          Number(
            value
          )
        )
          ? Number(
              value
            ).toFixed(
              3
            )
          : ""
      }
      inputProps={{
        step:
          "0.001",
      }}
      onBlur={(
        event
      ) =>
        updateCompactSetting(
          row,
          field,
          event.target.value
        )
      }
      onKeyDown={(
        event
      ) => {
        if (
          event.key ===
          "Enter"
        ) {
          event.currentTarget
            .blur();
        }
      }}
      sx={{
        "& input": {
          fontVariantNumeric:
            "tabular-nums",

          textAlign:
            "right",
        },
      }}
    />
  );


  const compactTeamSettings = () => (
    <Box
      sx={{
        mb: 2.5,
        border: 1,
        borderColor:
          "divider",
        borderRadius: 2,
        overflowX:
          "auto",
      }}
    >
      <Box
        sx={{
          minWidth: 760,
        }}
      >
        <Box
          sx={{
            display:
              "grid",

            gridTemplateColumns:
              "minmax(180px, 1.5fr) repeat(4, minmax(125px, 1fr))",

            gap: 1,
            px: 2,
            py: 1.25,

            borderBottom: 1,
            borderColor:
              "divider",

            bgcolor:
              "action.hover",

            alignItems:
              "center",
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight:
                700,
            }}
          >
            Team
          </Typography>

          <Tooltip
            arrow
            title="Spielstärke und Torquotient sind dieselbe Kennzahl. Sie beschreibt den erwarteten Torquotienten des Teams im geschlossenen System."
          >
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight:
                  700,

                cursor:
                  "help",
              }}
            >
              Spielstärke /
              Torquotient
            </Typography>
          </Tooltip>

          <Tooltip
            arrow
            title="Das erwartete Torniveau der Spiele dieses Teams im geschlossenen System."
          >
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight:
                  700,

                cursor:
                  "help",
              }}
            >
              Torniveau
            </Typography>
          </Tooltip>

          <Tooltip
            arrow
            title="Individueller Heimvorteil des Teams als gemeinsame Kennzahl. 1,000 entspricht neutral."
          >
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight:
                  700,

                cursor:
                  "help",
              }}
            >
              Heimvorteil
            </Typography>
          </Tooltip>

          <Tooltip
            arrow
            title="Individueller Unentschieden-Faktor des Teams. Er dient dazu, die Remishäufigkeit entsprechend unserer Einschätzung abzubilden."
          >
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight:
                  700,

                cursor:
                  "help",
              }}
            >
              Unentschieden-
              Faktor
            </Typography>
          </Tooltip>
        </Box>


        {rows.map(
          (row) => (
            <Box
              key={
                row.id
              }
              sx={{
                display:
                  "grid",

                gridTemplateColumns:
                  "minmax(180px, 1.5fr) repeat(4, minmax(125px, 1fr))",

                gap: 1,
                px: 2,
                py: 1,

                borderBottom:
                  1,

                borderColor:
                  "divider",

                alignItems:
                  "center",
              }}
            >
              <Typography
                sx={{
                  fontWeight:
                    600,
                }}
              >
                {row.name ||
                  row.team_name ||
                  row.custom_name ||
                  `Team ${row.id}`}
              </Typography>

              {compactNumberField(
                row,
                "total_strength",
                row.total_strength
              )}

              {compactNumberField(
                row,
                "total_goals",
                row.total_goals
              )}

              {compactNumberField(
                row,
                "home_advantage",
                getCombinedHomeAdvantage(
                  row
                )
              )}

              {compactNumberField(
                row,
                "draw_factor",
                row.draw_factor
              )}
            </Box>
          )
        )}
      </Box>


      <Box
        sx={{
          px: 2,
          py: 1.5,

          bgcolor:
            "action.hover",
        }}
      >
        <Typography
          variant="body2"
          color="text.secondary"
        >
          Änderungen gelten zunächst nur für
          diesen Simulationslauf. Dauerhaft
          übernommen werden sie erst durch eine
          ausdrückliche Speicheraktion.
        </Typography>
      </Box>
    </Box>
  );



  const handleNo = () => {
    const { oldRow, resolve } = promiseArguments;
    resolve(oldRow);
    setPromiseArguments(null);
  };

  const handleYes = async () => {
    const { newRow, oldRow, reject, resolve } = promiseArguments;

    try {
      const computedValues = computeInterdependentValues(newRow, oldRow);
      resolve(computedValues);
      setPromiseArguments(null);
      const updatedRow = { ...computedValues, isNew: false };
      setRows(
        rows.map((row) => (row.id === computedValues.id ? updatedRow : row))
      );
    } catch (error) {
      reject(oldRow);
      setPromiseArguments(null);
    }
  };

  useEffect(() => {
    if (promiseArguments && checked) {
      handleYes();
    }
  }, [promiseArguments, handleYes]);

  const handleCellEditCommit = () => {};

  const handleEntered = () => {};

  const adjustmentDefinitions = {
    goal_average: {
      title: "Toreschnitt",
      storedValue:
        Number(
          seasonParams?.stored_goal_average
        ),

      currentValue:
        average(rows, "total_goals"),

      fullWeightAt: 30,

      setFunction: setGoalAverage,
      adjustFunction: adjustGoalAverage,

      explanationSet:
        "Alle Mannschaften werden auf denselben Toreschnitt gesetzt. Das ist insbesondere vor einer neuen Saison sinnvoll, wenn Mannschaften aus unterschiedlichen Ligen mit nicht vergleichbaren Ausgangswerten zusammenkommen.",

      explanationAdjust:
        "Die vorhandenen Unterschiede zwischen den Mannschaften bleiben erhalten. Alle Werte werden proportional verschoben, bis der gewünschte Ligamittelwert erreicht ist.",
    },

    home_advantage: {
      title: "Heimvorteil",

      storedValue:
        Number(
          seasonParams?.exp_home_factor
        ),

      currentValue:
        average(
          rows,
          "for_homefactor"
        ),

      currentAgainstValue:
        average(
          rows,
          "against_homefactor"
        ),

      fullWeightAt: 30,

      setFunction: setHomeAdvantage,
      adjustFunction:
        adjustHomeAdvantage,

      explanationSet:
        "Alle Mannschaften erhalten denselben Heimfaktor. Der Gegenwert wird automatisch so gesetzt, dass beide Ligamittelwerte zusammen 2,000 ergeben.",

      explanationAdjust:
        "Die individuellen Heimprofile bleiben erhalten. Die For-Home- und Against-Home-Faktoren werden getrennt proportional auf die neuen Zielmittelwerte verschoben.",
    },

    draw_factor: {
      title: "Unentschieden-Faktor",

      storedValue:
        Number(
          seasonParams?.drawfactor
        ),

      currentValue:
        average(rows, "draw_factor"),

      fullWeightAt: 30,

      setFunction: setDrawFactor,
      adjustFunction: adjustDrawFactor,

      explanationSet:
        "Alle Mannschaften erhalten denselben Unentschieden-Faktor.",

      explanationAdjust:
        "Die bisherigen Unterschiede bleiben erhalten. Alle Unentschieden-Faktoren werden proportional auf den gewünschten Mittelwert verschoben.",
    },
  };

  function openAdjustmentMenu(
    event,
    type
  ) {
    setAdjustmentMenuAnchor(
      event.currentTarget
    );

    setAdjustmentMenuType(type);
  }

  function closeAdjustmentMenu() {
    setAdjustmentMenuAnchor(null);
    setAdjustmentMenuType(null);
  }

  function openAdjustmentDialog(mode) {
    const definition =
      adjustmentDefinitions[
        adjustmentMenuType
      ];

    const initialTarget =
      Number.isFinite(
        definition?.storedValue
      )
        ? definition.storedValue
        : definition?.currentValue;

    setTargetValue(
      Number.isFinite(initialTarget)
        ? String(initialTarget)
        : ""
    );

    setAdjustmentDialog({
      type: adjustmentMenuType,
      mode,
    });

    closeAdjustmentMenu();
  }

  function closeAdjustmentDialog() {
    setAdjustmentDialog(null);
  }

  function getRecommendation() {
    if (!adjustmentDialog) {
      return null;
    }

    const definition =
      adjustmentDefinitions[
        adjustmentDialog.type
      ];

    /*
     * Der Draw-Factor besitzt seine eigene
     * Empfehlungslogik auf Basis der
     * Vorsaison-Abweichung zwischen
     * erwarteten und eingetroffenen Remis.
     */
    if (
      adjustmentDialog.type ===
      "draw_factor"
    ) {
      const drawRecommendation =
        getPreviousSeasonDrawRecommendation();

      return Number.isFinite(
        drawRecommendation
      )
        ? drawRecommendation
        : definition.storedValue;
    }

    const previousSeasonValue =
      getPreviousSeasonValue(
        adjustmentDialog.type
      );

    /*
     * Für Toreschnitt und Heimvorteil:
     *
     * Vorsaison vorhanden:
     *   1/3 gespeicherter Wert
     * + 2/3 tatsächlich eingetroffener
     *   Wert der Vorsaison.
     *
     * Keine Vorsaison vorhanden:
     *   gespeicherter Wert bleibt die
     *   Engine-Empfehlung.
     */
    if (
      Number.isFinite(
        definition.storedValue
      ) &&
      Number.isFinite(
        previousSeasonValue
      )
    ) {
      return (
        definition.storedValue +
        2 * previousSeasonValue
      ) / 3;
    }

    if (
      Number.isFinite(
        previousSeasonValue
      )
    ) {
      return previousSeasonValue;
    }

    return Number.isFinite(
      definition.storedValue
    )
      ? definition.storedValue
      : null;
  }

  function applyRecommendation() {
    const recommendation =
      getRecommendation();

    if (
      Number.isFinite(recommendation)
    ) {
      setTargetValue(
        String(
          Number(
            recommendation.toFixed(3)
          )
        )
      );
    }
  }

  function applyAdjustment() {
    if (!adjustmentDialog) return;

    const target =
      Number(targetValue);

    if (!Number.isFinite(target)) {
      setSnackbar({
        severity: "error",
        children:
          "Bitte einen gültigen Zielwert eingeben.",
      });

      return;
    }

    const definition =
      adjustmentDefinitions[
        adjustmentDialog.type
      ];

    try {
      const updatedRows =
        adjustmentDialog.mode === "set"
          ? definition.setFunction(
              rows,
              target
            )
          : definition.adjustFunction(
              rows,
              target
            );

      setRows(
        sortAndRankTeams(updatedRows)
      );

      setHasChanges(true);

      setSnackbar({
        severity: "success",
        children:
          "Die Werte wurden im Editor angepasst. Sie werden erst mit Save dauerhaft gespeichert.",
      });

      closeAdjustmentDialog();
    } catch (error) {
      setSnackbar({
        severity: "error",
        children:
          error?.message ||
          String(error),
      });
    }
  }


  function openNormalizationMenu(event) {
    setNormalizationMenuAnchor(
      event.currentTarget
    );
  }

  function closeNormalizationMenu() {
    setNormalizationMenuAnchor(null);
  }

  function openNormalizationDialog(type) {
    setNormalizationDialog(type);
    closeNormalizationMenu();
  }

  function closeNormalizationDialog() {
    setNormalizationDialog(null);
  }

  function applyNormalization() {
    try {
      const updatedRows =
        normalizationDialog ===
        "goal_totals"
          ? normalizeGoalTotals(rows)
          : normalizeHomeAdvantage(rows);

      setRows(
        sortAndRankTeams(updatedRows)
      );

      setHasChanges(true);

      setSnackbar({
        severity: "success",
        children:
          "Die Team Settings wurden im Editor normiert. Dauerhaft gespeichert werden sie erst über Save.",
      });

      closeNormalizationDialog();
    } catch (error) {
      setSnackbar({
        severity: "error",
        children:
          error?.message ||
          String(error),
      });
    }
  }


  const renderConfirmDialog = () => {
    if (!promiseArguments) {
      return null;
    }

    const { newRow, oldRow } = promiseArguments;
    const mutation = computeTeamStrengthMutation(newRow, oldRow);

    return (
      <Dialog
        maxWidth="xs"
        TransitionProps={{ onEntered: handleEntered }}
        open={!!promiseArguments}
      >
        <DialogTitle>Are you sure?</DialogTitle>
        <DialogContent dividers>
          {`Pressing 'Yes' will change ${mutation}. Keep in mind: the changes will not be stored to your data set before you select the "Save" button to the top right`}
          <FormControlLabel
            control={<Checkbox checked={checked} onChange={handleChange} />}
            label="Don't show again during this dialog"
          />
        </DialogContent>
        <DialogActions>
          <Button ref={noButtonRef} onClick={handleNo}>
            No
          </Button>
          <Button onClick={handleYes}>Yes</Button>
        </DialogActions>
      </Dialog>
    );
  };
  const theme = useTheme();

  const sumForTotal = rows.reduce(
    (sum, row) =>
      sum + Number(row.for_total || 0),
    0
  );

  const sumAgainstTotal = rows.reduce(
    (sum, row) =>
      sum + Number(row.against_total || 0),
    0
  );

  const totalDifference =
    sumForTotal - sumAgainstTotal;

  const averageForHome =
    average(rows, "for_homefactor");

  const averageAgainstHome =
    average(rows, "against_homefactor");

  const homeFactorSum =
    Number.isFinite(averageForHome) &&
    Number.isFinite(averageAgainstHome)
      ? averageForHome +
        averageAgainstHome
      : null;

  const goalTotalsAreNormalized =
    Math.abs(totalDifference) < 0.001;

  const homeAdvantageIsNormalized =
    Number.isFinite(homeFactorSum) &&
    Math.abs(homeFactorSum - 2) <
      0.001;

  return (
    <Box
      data-team-settings-mode={
        isSimulationMode
          ? "simulation"
          : "full"
      }
      sx={{
        width: "100%",
        "& .total-strength": {
          backgroundColor: theme.palette.primary.light,
          color: "white",
          fontWeight: "bold",
          fontSize: "1.5em",
        },
        "& .total-goals": {
          backgroundColor: theme.palette.secondary.light,
          color: "white",
          fontWeight: "600",
          fontSize: "1.5em",
        },
      }}
    >
      {renderConfirmDialog()}
      {panelHeader()}

      {isSimulationMode
        ? compactTeamSettings()
        : (
          <DataGridPro
            autoHeight
            onCellEditStop={
              handleCellEditCommit
            }
            rows={rows}
            columns={
              teamStrengthColumns
            }
            processRowUpdate={
              processRowUpdate
            }
            experimentalFeatures={{
              newEditingApi: true,
            }}
            ref={apiRef}
          />
        )}
      <Dialog
        open={Boolean(adjustmentDialog)}
        onClose={closeAdjustmentDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {adjustmentDialog
            ? `${
                adjustmentDefinitions[
                  adjustmentDialog.type
                ]?.title
              } – ${
                adjustmentDialog.mode ===
                "set"
                  ? "Auf Zielwert setzen"
                  : "Ligamittel relativ anpassen"
              }`
            : ""}
        </DialogTitle>

        <DialogContent dividers>
          {adjustmentDialog && (() => {
            const definition =
              adjustmentDefinitions[
                adjustmentDialog.type
              ];

            const recommendation =
              getRecommendation();

            const isHomeAdvantage =
              adjustmentDialog.type ===
              "home_advantage";

            return (
              <Stack spacing={2}>
                <Typography>
                  {adjustmentDialog.mode ===
                  "set"
                    ? definition.explanationSet
                    : definition.explanationAdjust}
                </Typography>

                <Divider />

                <Typography>
                  <strong>
                    Gespeicherter Wert:
                  </strong>{" "}
                  {Number.isFinite(
                    definition.storedValue
                  )
                    ? definition.storedValue.toFixed(
                        3
                      )
                    : "nicht verfügbar"}
                </Typography>

                <Typography>
                  <strong>
                    Aktueller Mittelwert der Einstellungen:
                  </strong>{" "}
                  {Number.isFinite(
                    definition.currentValue
                  )
                    ? definition.currentValue.toFixed(
                        3
                      )
                    : "nicht verfügbar"}
                </Typography>

                {isHomeAdvantage && (
                  <Typography>
                    <strong>
                      Aktueller Against-Home-Mittelwert:
                    </strong>{" "}
                    {Number.isFinite(
                      definition.currentAgainstValue
                    )
                      ? definition.currentAgainstValue.toFixed(
                          3
                        )
                      : "nicht verfügbar"}
                  </Typography>
                )}

                {adjustmentDialog.type ===
                  "draw_factor" ? (
                  <Box>
                    <Typography>
                      <strong>
                        Vorsaison – Remis erwartet:
                      </strong>{" "}
                      {Number.isFinite(
                        Number(
                          previousSeasonReference
                            ?.aggregate
                            ?.model_comparison
                            ?.expected_draw_rate
                        )
                      )
                        ? `${(
                            Number(
                              previousSeasonReference
                                .aggregate
                                .model_comparison
                                .expected_draw_rate
                            ) * 100
                          ).toFixed(2)} %`
                        : "nicht verfügbar"}
                    </Typography>

                    <Typography>
                      <strong>
                        Vorsaison – Remis eingetroffen:
                      </strong>{" "}
                      {Number.isFinite(
                        Number(
                          previousSeasonReference
                            ?.aggregate
                            ?.model_comparison
                            ?.actual
                            ?.draw_rate
                        )
                      )
                        ? `${(
                            Number(
                              previousSeasonReference
                                .aggregate
                                .model_comparison
                                .actual
                                .draw_rate
                            ) * 100
                          ).toFixed(2)} %`
                        : "nicht verfügbar"}
                    </Typography>

                    {previousSeason &&
                      !previousSeasonReference &&
                      !loadingPreviousSeason && (
                        <Button
                          variant="outlined"
                          size="small"
                          sx={{ mt: 1 }}
                          disabled={
                            calculatingPreviousSeason
                          }
                          onClick={
                            calculatePreviousSeasonReference
                          }
                        >
                          {calculatingPreviousSeason
                            ? "Vorsaison wird berechnet …"
                            : "Vorsaison berechnen"}
                        </Button>
                      )}
                  </Box>
                ) : (
                  <Box>
                    <Typography>
                      <strong>
                        Vorsaison:
                      </strong>{" "}

                      {loadingPreviousSeason
                        ? "wird geladen …"
                        : !previousSeason
                          ? "keine Vorsaison gefunden"
                          : Number.isFinite(
                              getPreviousSeasonValue(
                                adjustmentDialog.type
                              )
                            )
                            ? `${
                                previousSeason.name ||
                                `Season ${previousSeason.id}`
                              }: ${getPreviousSeasonValue(
                                adjustmentDialog.type
                              ).toFixed(3)}`
                            : `${
                                previousSeason.name ||
                                `Season ${previousSeason.id}`
                              }: noch kein League-Reference-Record vorhanden`}
                    </Typography>

                    {!loadingPreviousSeason &&
                      previousSeason &&
                      !Number.isFinite(
                        getPreviousSeasonValue(
                          adjustmentDialog.type
                        )
                      ) && (
                        <Button
                          variant="outlined"
                          size="small"
                          sx={{ mt: 1 }}
                          disabled={
                            calculatingPreviousSeason
                          }
                          onClick={
                            calculatePreviousSeasonReference
                          }
                        >
                          {calculatingPreviousSeason
                            ? "Vorsaison wird berechnet …"
                            : "Vorsaison berechnen"}
                        </Button>
                      )}
                  </Box>
                )}

                <Divider />

                <Typography>
                  <strong>
                    Engine-Empfehlung:
                  </strong>{" "}
                  {Number.isFinite(
                    recommendation
                  )
                    ? recommendation.toFixed(
                        3
                      )
                    : "nicht verfügbar"}
                </Typography>

                <Button
                  variant="outlined"
                  disabled={
                    !Number.isFinite(
                      recommendation
                    )
                  }
                  onClick={applyRecommendation}
                >
                  Engine-Empfehlung übernehmen
                </Button>

                <TextField
                  label="Gewählter Zielwert"
                  type="number"
                  value={targetValue}
                  onChange={(event) =>
                    setTargetValue(
                      event.target.value
                    )
                  }
                  inputProps={{
                    step: "0.001",
                  }}
                  fullWidth
                />

                {isHomeAdvantage &&
                  Number.isFinite(
                    Number(targetValue)
                  ) && (
                    <Alert severity="info">
                      Zielwerte: For Home{" "}
                      {Number(
                        targetValue
                      ).toFixed(3)} und
                      Against Home{" "}
                      {(
                        2 -
                        Number(targetValue)
                      ).toFixed(3)}.
                    </Alert>
                  )}

                <Alert severity="info">
                  Die Änderungen erscheinen
                  zunächst nur im Editor. Sie
                  werden erst mit dem Save-Button
                  oben dauerhaft gespeichert.
                  Danach können sämtliche Werte
                  weiterhin einzeln manuell
                  verändert werden.
                </Alert>
              </Stack>
            );
          })()}
        </DialogContent>

        <DialogActions>
          <Button
            onClick={closeAdjustmentDialog}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={applyAdjustment}
          >
            Anwenden
          </Button>
        </DialogActions>
      </Dialog>

      <Box
        sx={{
          mt: 3,
          mb: 2,
          p: 2.5,
          border: 1,
          borderColor: "divider",
          borderRadius: 1,
        }}
      >
        <Typography
          variant="h6"
          sx={{ mb: 0.5 }}
        >
          Team Settings Utilities
        </Typography>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 2 }}
        >
          Häufig benötigte Hilfsfunktionen
          für die Einstellungen der gesamten
          Liga. Alle Änderungen erscheinen
          zunächst nur im Editor und werden
          erst über Save dauerhaft gespeichert.
        </Typography>

        <Box
          sx={{
            display:
              "grid",

            gridTemplateColumns: {
              xs:
                "1fr",

              sm:
                "repeat(2, minmax(0, 1fr))",

              lg:
                "repeat(4, minmax(0, 1fr))",
            },

            gap:
              1.5,

            mb:
              2.5,
          }}
        >
          {[
            {
              title:
                "Torschnitt anpassen",

              text:
                "Setze einen Zielwert oder verschiebe das Ligamittel, ohne die Unterschiede zwischen den Teams zu verlieren.",

              action:
                (event) =>
                  openAdjustmentMenu(
                    event,
                    "goal_average"
                  ),
            },

            {
              title:
                "Heimvorteil anpassen",

              text:
                "Passe das Heimniveau der Liga an. Die individuellen Unterschiede zwischen den Teams können dabei erhalten bleiben.",

              action:
                (event) =>
                  openAdjustmentMenu(
                    event,
                    "home_advantage"
                  ),
            },

            {
              title:
                "Unentschieden-Faktor anpassen",

              text:
                "Passe die Remis-Neigung der Liga an oder übernimm eine vorhandene Empfehlung.",

              action:
                (event) =>
                  openAdjustmentMenu(
                    event,
                    "draw_factor"
                  ),
            },

            {
              title:
                "Settings normieren",

              text:
                "Gleiche kleine mathematische Abweichungen bei Torwerten oder Heimfaktoren aus, ohne die Teamstruktur neu aufzubauen.",

              action:
                openNormalizationMenu,
            },
          ].map(
            (
              item
            ) => (
              <Paper
                key={
                  item.title
                }

                variant="outlined"

                role="button"

                tabIndex={
                  0
                }

                onClick={
                  item.action
                }

                sx={{
                  p:
                    2,

                  minHeight:
                    145,

                  cursor:
                    "pointer",

                  transition:
                    "transform 120ms ease, box-shadow 120ms ease, border-color 120ms ease",

                  "&:hover": {
                    transform:
                      "translateY(-2px)",

                    boxShadow:
                      3,

                    borderColor:
                      "primary.main",
                  },
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight:
                      700,

                    mb:
                      0.75,
                  }}
                >
                  {
                    item.title
                  }
                </Typography>

                <Typography
                  variant="body2"
                  color="text.secondary"
                >
                  {
                    item.text
                  }
                </Typography>
              </Paper>
            )
          )}
        </Box>


        <Menu
          anchorEl={adjustmentMenuAnchor}
          open={Boolean(
            adjustmentMenuAnchor
          )}
          onClose={closeAdjustmentMenu}
        >
          <MenuItem
            onClick={() =>
              openAdjustmentDialog("set")
            }
          >
            Auf Zielwert setzen
          </MenuItem>

          <MenuItem
            onClick={() =>
              openAdjustmentDialog(
                "relative"
              )
            }
          >
            Ligamittel relativ anpassen
          </MenuItem>
        </Menu>

        <Menu
          anchorEl={normalizationMenuAnchor}
          open={Boolean(
            normalizationMenuAnchor
          )}
          onClose={closeNormalizationMenu}
        >
          <MenuItem
            onClick={() =>
              openNormalizationDialog(
                "goal_totals"
              )
            }
          >
            Tore normieren
          </MenuItem>

          <MenuItem
            onClick={() =>
              openNormalizationDialog(
                "home_advantage"
              )
            }
          >
            Heimvorteil normieren
          </MenuItem>
        </Menu>

        <Divider sx={{ mb: 2 }} />

        <Button
          size="small"
          variant="text"
          onClick={() =>
            setConsistencyOpen(
              (value) =>
                !value
            )
          }
          sx={{
            px: 0,
            mb:
              consistencyOpen
                ? 1.5
                : 0,
          }}
        >
          {consistencyOpen
            ? "Technische Konsistenz ausblenden"
            : "Technische Konsistenz anzeigen"}
        </Button>

        <Collapse
          in={
            consistencyOpen
          }
        >
          <Typography
            variant="subtitle1"
            sx={{ mb: 1 }}
          >
            Konsistenz der aktuellen Einstellungen
          </Typography>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns:
              "minmax(220px, 1fr) auto",
            columnGap: 3,
            rowGap: 0.75,
            maxWidth: 620,
          }}
        >
          <Typography>
            Summe For Total
          </Typography>
          <Typography
            sx={{
              fontVariantNumeric:
                "tabular-nums",
            }}
          >
            {sumForTotal.toFixed(3)}
          </Typography>

          <Typography>
            Summe Against Total
          </Typography>
          <Typography
            sx={{
              fontVariantNumeric:
                "tabular-nums",
            }}
          >
            {sumAgainstTotal.toFixed(3)}
          </Typography>

          <Typography>
            Differenz
          </Typography>
          <Typography
            sx={{
              fontVariantNumeric:
                "tabular-nums",
              fontWeight: 600,
            }}
          >
            {totalDifference >= 0
              ? "+"
              : ""}
            {totalDifference.toFixed(3)}
          </Typography>

          <Typography>
            Durchschnitt For Home Factor
          </Typography>
          <Typography
            sx={{
              fontVariantNumeric:
                "tabular-nums",
            }}
          >
            {Number.isFinite(
              averageForHome
            )
              ? averageForHome.toFixed(3)
              : "–"}
          </Typography>

          <Typography>
            Durchschnitt Against Home Factor
          </Typography>
          <Typography
            sx={{
              fontVariantNumeric:
                "tabular-nums",
            }}
          >
            {Number.isFinite(
              averageAgainstHome
            )
              ? averageAgainstHome.toFixed(
                  3
                )
              : "–"}
          </Typography>

          <Typography>
            Summe der Heimfaktoren
          </Typography>
          <Typography
            sx={{
              fontVariantNumeric:
                "tabular-nums",
              fontWeight: 600,
            }}
          >
            {Number.isFinite(
              homeFactorSum
            )
              ? homeFactorSum.toFixed(3)
              : "–"}
          </Typography>
        </Box>

        {!goalTotalsAreNormalized && (
          <Alert
            severity="info"
            sx={{ mt: 2 }}
          >
            For Total und Against Total
            besitzen derzeit nicht exakt
            dieselbe Summe. Eine Normierung
            ist mathematisch sauberer, aber
            die kleine Abweichung verursacht
            keinen schwerwiegenden
            Berechnungsfehler.
          </Alert>
        )}

        {!homeAdvantageIsNormalized && (
          <Alert
            severity="info"
            sx={{ mt: 1.5 }}
          >
            Die Mittelwerte von For Home
            Factor und Against Home Factor
            ergeben derzeit nicht exakt
            2,000. Der Heimvorteil kann bei
            Bedarf normiert werden.
          </Alert>
        )}

        {goalTotalsAreNormalized &&
          homeAdvantageIsNormalized && (
            <Alert
              severity="success"
              sx={{ mt: 2 }}
            >
              Die angezeigten Werte sind
              normiert.
            </Alert>
          )}
        </Collapse>
      </Box>

      <Dialog
        open={Boolean(
          normalizationDialog
        )}
        onClose={
          closeNormalizationDialog
        }
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {normalizationDialog ===
          "goal_totals"
            ? "Tore normieren"
            : "Heimvorteil normieren"}
        </DialogTitle>

        <DialogContent dividers>
          {normalizationDialog ===
          "goal_totals" ? (
            <Stack spacing={2}>
              <Typography>
                Die Summe aller For-Total-
                Werte und die Summe aller
                Against-Total-Werte werden
                auf ihren gemeinsamen
                Mittelwert gebracht.
              </Typography>

              <Typography>
                Die relativen Unterschiede
                zwischen den Mannschaften
                bleiben erhalten.
              </Typography>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns:
                    "1fr auto",
                  gap: 1,
                }}
              >
                <Typography>
                  Aktuelle Summe For Total
                </Typography>
                <Typography>
                  {sumForTotal.toFixed(3)}
                </Typography>

                <Typography>
                  Aktuelle Summe Against Total
                </Typography>
                <Typography>
                  {sumAgainstTotal.toFixed(3)}
                </Typography>

                <Typography
                  sx={{ fontWeight: 600 }}
                >
                  Neuer gemeinsamer Wert
                </Typography>
                <Typography
                  sx={{ fontWeight: 600 }}
                >
                  {(
                    (sumForTotal +
                      sumAgainstTotal) /
                    2
                  ).toFixed(3)}
                </Typography>
              </Box>

              <Alert severity="info">
                Eine Normierung hält die
                Team Settings mathematisch
                sauber. Kleine Abweichungen
                sind jedoch nicht gefährlich
                und verursachen keinen
                schwerwiegenden Fehler.
              </Alert>
            </Stack>
          ) : (
            <Stack spacing={2}>
              <Typography>
                Die For-Home- und
                Against-Home-Faktoren werden
                gemeinsam proportional
                skaliert.
              </Typography>

              <Typography>
                Ihre individuellen
                Unterschiede bleiben
                erhalten. Die Summe ihrer
                Mittelwerte beträgt danach
                exakt 2,000.
              </Typography>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns:
                    "1fr auto",
                  gap: 1,
                }}
              >
                <Typography>
                  Durchschnitt For Home
                </Typography>
                <Typography>
                  {Number.isFinite(
                    averageForHome
                  )
                    ? averageForHome.toFixed(
                        3
                      )
                    : "–"}
                </Typography>

                <Typography>
                  Durchschnitt Against Home
                </Typography>
                <Typography>
                  {Number.isFinite(
                    averageAgainstHome
                  )
                    ? averageAgainstHome.toFixed(
                        3
                      )
                    : "–"}
                </Typography>

                <Typography
                  sx={{ fontWeight: 600 }}
                >
                  Aktuelle Summe
                </Typography>
                <Typography
                  sx={{ fontWeight: 600 }}
                >
                  {Number.isFinite(
                    homeFactorSum
                  )
                    ? homeFactorSum.toFixed(
                        3
                      )
                    : "–"}
                </Typography>

                <Typography
                  sx={{ fontWeight: 600 }}
                >
                  Neue Summe
                </Typography>
                <Typography
                  sx={{ fontWeight: 600 }}
                >
                  2.000
                </Typography>
              </Box>

              <Alert severity="info">
                Kleine Abweichungen sind
                unproblematisch. Die
                Normierung sorgt lediglich
                dafür, dass der angezeigte
                Heimvorteil mathematisch
                exakt zu den Team Settings
                passt.
              </Alert>
            </Stack>
          )}
        </DialogContent>

        <DialogActions>
          <Button
            onClick={
              closeNormalizationDialog
            }
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={applyNormalization}
          >
            Normieren
          </Button>
        </DialogActions>
      </Dialog>

      {!!snackbar && (
        <Snackbar open onClose={handleCloseSnackbar} autoHideDuration={6000}>
          <Alert {...snackbar} onClose={handleCloseSnackbar}/>
        </Snackbar>
      )}
      <Box
        sx={{
          mt: 2,
          p: 2,
          border: 1,
          borderColor:
            "divider",
          borderRadius: 2,
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight:
              700,
          }}
        >
          Settings zurücksetzen / wiederherstellen
        </Typography>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            mt:
              0.5,

            mb:
              1.5,
          }}
        >
          Wenn du deine eigenen Team Settings
          verwerfen möchtest, kannst du sie hier
          löschen. Danach können wieder die für
          die Liga bereitgestellten Settings
          geladen werden.
        </Typography>

        <Button
          variant="outlined"
          onClick={
            handleClickOpen
          }
          startIcon={
            <DeleteIcon />
          }
          color="error"
        >
          Eigene Settings löschen
        </Button>
      </Box>
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {"Eigene Team Settings löschen?"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Deine eigenen Team Settings für diese Liga werden gelöscht. Danach kannst du wieder die bereitgestellten Settings laden. Diese Aktion ist zum Zurücksetzen gedacht, wenn du deine eigenen Einstellungen verwerfen möchtest.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={
              handleClose
            }
          >
            Abbrechen
          </Button>

          <Button
            onClick={
              handleClickDelete
            }
            color="error"
            variant="contained"
          >
            Eigene Settings löschen
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
